import React from 'react';
import { motion } from 'framer-motion';
import { Car, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';

const Footer: React.FC = () => {
  const footerLinks = [
    {
      title: 'Product',
      links: [
        { name: 'Features', href: '#features' },
        { name: 'Pricing', href: '#pricing' },
        { name: 'Testimonials', href: '#testimonials' },
        { name: 'Roadmap', href: '#roadmap' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Blog', href: '#blog' },
        { name: 'Guides', href: '#guides' },
        { name: 'Support', href: '#support' },
        { name: 'Training', href: '#training' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About', href: '#about' },
        { name: 'Team', href: '#team' },
        { name: 'Contact', href: '#contact' },
        { name: 'Partners', href: '#partners' }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const socialLinks = [
    { icon: <Facebook size={18} />, href: '#' },
    { icon: <Twitter size={18} />, href: '#' },
    { icon: <Instagram size={18} />, href: '#' },
    { icon: <Linkedin size={18} />, href: '#' }
  ];

  return (
    <footer className="bg-background/70 backdrop-blur-md border-t border-white/10 pt-16 pb-8">
      <div className="container mx-auto px-4">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div className="lg:col-span-2" variants={itemVariants}>
            <div className="flex items-center space-x-3 mb-6">
              <Car className="w-8 h-8 text-accent" />
              <span className="text-xl font-extrabold">DetailerOS</span>
            </div>
            <p className="text-white/70 mb-6 max-w-md">
              The all-in-one platform for detailing businesses to manage clients, streamline operations, and scale their services with powerful business growth tools.
            </p>
          </motion.div>
          
          {footerLinks.map((section, index) => (
            <motion.div key={index} variants={itemVariants}>
              <h4 className="font-bold text-lg mb-4">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a 
                      href={link.href} 
                      className="text-white/70 hover:text-white hover:translate-x-1 inline-block transition-transform duration-200"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div 
          className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="text-white/50 text-sm mb-4 md:mb-0">
            © {new Date().getFullYear()} DetailerOS, Inc. All rights reserved.
          </div>
          
          <div className="flex space-x-4 mb-4 md:mb-0">
            {socialLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.href}
                className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-accent hover:text-background"
                whileHover={{ scale: 1.1, backgroundColor: '#CCFF00', color: '#121212' }}
                whileTap={{ scale: 0.9 }}
              >
                {link.icon}
              </motion.a>
            ))}
          </div>
          
          <div className="flex space-x-4 text-white/50 text-sm">
            <a href="#privacy" className="hover:text-white">Privacy Policy</a>
            <a href="#terms" className="hover:text-white">Terms of Service</a>
            <a href="#cookies" className="hover:text-white">Cookies</a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;